/**
 * AppBreadcrumbs — Shared breadcrumb component used on every page.
 *
 * Usage:
 *   <AppBreadcrumbs />                              — auto-generates from URL
 *   <AppBreadcrumbs extra={[{ label, path }]} />    — appends extra crumbs
 *   <AppBreadcrumbs current="Server SRV-001" />     — overrides last crumb label
 *
 * Examples:
 *   /cmdb/list             → CMDB > Configuration Items
 *   /cmdb/abc123           → CMDB > Configuration Items > [id]
 *   /problems/abc123       → Problem Management > Problems > [id]
 *   /knowledge/articles    → Problem Management > Knowledge Base > Articles
 */
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { BREADCRUMB_LABELS, SECTION_ANCESTORS, resolveBreadcrumbTrail } from '../config/navConfig';

// Segments that are pure UUIDs / MongoDB ObjectIds — show as "Details" or skip
const OBJECT_ID_RE = /^[a-f0-9]{24}$|^[0-9a-f-]{36}$/i;

function humanise(segment) {
  if (BREADCRUMB_LABELS[segment]) return BREADCRUMB_LABELS[segment];
  if (OBJECT_ID_RE.test(segment)) return 'Details';
  // Title-case any remaining segments
  return segment
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function AppBreadcrumbs({ current, extra = [] }) {
  const { pathname } = useLocation();

  // Split and clean path segments
  const raw = pathname.split('/').filter(Boolean);
  if (raw.length === 0) return null;

  // Build crumb list
  const crumbs = [];

  // Always start with Dashboard
  crumbs.push({ label: 'Dashboard', path: '/home' });

  // ── Primary: derive the trail from the navigation tree so the breadcrumb
  //    always mirrors the sidebar grouping (group → item → sub-item). ──
  const navTrail = resolveBreadcrumbTrail(pathname);

  if (navTrail && navTrail.length) {
    navTrail.forEach((c, i) => {
      const isLast = i === navTrail.length - 1;
      crumbs.push({ label: c.label, path: isLast ? null : c.path });
    });

    // Append any extra URL segments beyond the matched nav item (e.g. a detail
    // id after /cmdb/list) so deep routes still show context.
    const matched = navTrail[navTrail.length - 1];
    if (matched.path) {
      const matchedSegs = matched.path.split('/').filter(Boolean);
      const extraSegs = raw.slice(matchedSegs.length);
      let cumPath = matched.path.replace(/\/+$/, '');
      extraSegs.forEach((seg, idx) => {
        cumPath += '/' + seg;
        const isLast = idx === extraSegs.length - 1;
        crumbs.push({ label: humanise(seg), path: isLast ? null : cumPath });
      });
    }
  } else {
    // ── Fallback: no nav match (e.g. a dynamic/detail route). Use the legacy
    //    section-ancestor + humanised-segment approach. ──
    const firstSeg = raw[0];
    const ancestor = SECTION_ANCESTORS[firstSeg];
    if (ancestor) {
      crumbs.push({ label: ancestor.label, path: ancestor.path });
    }

    let cumPath = '';
    raw.forEach((seg, idx) => {
      cumPath += '/' + seg;
      const isLast = idx === raw.length - 1;
      const label = humanise(seg);

      if (
        seg === 'home' ||
        seg === 'dashboard' ||
        (idx === 0 && ancestor)
      ) return;

      crumbs.push({ label, path: isLast ? null : cumPath });
    });
  }

  // Append any manually-provided extra crumbs
  extra.forEach(c => crumbs.push({ label: c.label, path: c.path || null }));

  // Override the last crumb label if `current` prop provided
  if (current && crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { label: current, path: null };
  }

  // Deduplicate consecutive identical labels
  const deduped = crumbs.filter((c, i) =>
    i === 0 || c.label !== crumbs[i - 1].label
  );

  if (deduped.length <= 1) return null;

  return (
    <Box mb={0.5}>
      <Breadcrumbs
        separator={<NavigateNext sx={{ fontSize: 14 }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap', rowGap: '2px' },
          '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'text.disabled' },
        }}
      >
        {deduped.map((crumb, idx) => {
          const isLast = idx === deduped.length - 1;
          if (isLast || !crumb.path) {
            return (
              <Typography
                key={idx}
                variant="caption"
                fontWeight={isLast ? 600 : 400}
                color={isLast ? 'text.primary' : 'text.secondary'}
                sx={{ lineHeight: 1.4 }}
              >
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={idx}
              component={RouterLink}
              to={crumb.path}
              underline="hover"
              color="text.secondary"
              variant="caption"
              sx={{ lineHeight: 1.4, '&:hover': { color: 'primary.main' } }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
